import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, Input, Textarea, Btn } from "@/components/UI";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import { TrDateField } from "@/components/common/TrDateField";
import { CustomerTypeBadge } from "@/components/customers/CustomerTypeBadge";
import { OrderLinesEditor, emptyLine } from "@/components/sales/OrderLinesEditor";
import { calcTotals, PRICE_FIELD } from "@/lib/sales";
import { fmtNum } from "@/lib/products";
import { apiError } from "@/lib/customers";

const localIso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
const today = () => localIso(new Date());
const addDays = (iso, d) => { const x = new Date(`${iso}T00:00:00`); x.setDate(x.getDate() + (Number(d) || 0)); return localIso(x); };
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function TotalsBox({ lines, f }) {
  const t = calcTotals(lines);
  const row = (l, v, testid, bold) => <div className={`flex justify-between ${bold ? "font-bold text-base border-t pt-1 mt-1" : ""}`}><span>{l}</span><span data-testid={testid}>{fmtNum(v)} TRY</span></div>;
  return (
    <Card className="p-4 text-sm space-y-0.5" data-testid="so-totals">
      {row("Brüt", t.gross, "so-total-gross")}{row("İskonto", -t.discount, "so-total-discount")}
      {row("Ara Toplam", t.net, "so-total-net")}{row("KDV", t.vat, "so-total-vat")}{row("Genel Toplam", t.grand_total, "so-total-grand", true)}
      <div className="text-[11px] text-slate-500 pt-1">Vade tarihi: {f.order_date ? addDays(f.order_date, f.due_days).split("-").reverse().join(".") : "—"}</div>
    </Card>
  );
}

function TelegramReviewBanner({ src }) {
  if (src.source !== "TELEGRAM" || (!src.needsMatch && !src.pending.length)) return null;
  return (
    <Card className="p-3 mb-4 border-amber-300 bg-amber-50 text-xs text-amber-900 space-y-1" data-testid="so-form-telegram-review">
      <div className="font-semibold">Telegram'dan gelen taslak — kontrol gerekli</div>
      {src.needsMatch && <div data-testid="so-form-needs-customer">Müşteri eşleşmesi gerekli{src.tg?.username ? ` (Telegram: @${src.tg.username})` : ""} — listeden müşteri seçin.</div>}
      {src.pending.map((p, i) => (
        <div key={i} data-testid={`so-form-pending-${i}`}>Eşleşmeyen ürün: <b>{p.text}</b> {p.qty ? `(${p.qty} adet)` : ""}
          {(p.candidates || []).length > 1 && <> — olası: {p.candidates.map((c) => c.code || c.name).join(", ")}</>}
        </div>
      ))}
      {src.tg?.text && <div className="text-amber-800/80 italic">“{src.tg.text}”</div>}
    </Card>
  );
}

export default function SalesOrderFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [vatDefault, setVatDefault] = useState(20);
  const [cust, setCust] = useState(null);
  const [custText, setCustText] = useState("");
  const [f, setF] = useState({ customer_ref: "", order_date: today(), delivery_date: "", due_days: "", show_discount: true, notes: "" });
  const [lines, setLines] = useState([emptyLine()]);
  const [src, setSrc] = useState({ source: "MANUAL", pending: [], needsMatch: false, tg: null });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/customers", { active: true }), api.get("/products", { active: true }), api.get("/company-profile")])
      .then(([cs, ps, cp]) => { setCustomers(cs); setProducts(ps); setVatDefault(Number(cp.default_vat_rate ?? 20)); })
      .catch((e) => setErr(apiError(e, "Veriler yüklenemedi")));
    if (!id) return;
    api.get(`/sales-orders/${id}`).then((so) => {
      setCust(so.customer_id ? { id: so.customer_id, ...so.customer_snapshot } : null);
      setCustText(so.customer_id ? (so.customer_snapshot?.legal_name || so.customer_name || "") : "");
      setSrc({ source: so.source || "MANUAL", pending: so.pending_lines || [], needsMatch: !!so.needs_customer_match, tg: so.telegram || null });
      setF({ customer_ref: so.customer_ref || "", order_date: so.order_date, delivery_date: so.delivery_date || "", due_days: so.due_days, show_discount: so.show_discount, notes: so.notes || "" });
      const pend = (so.pending_lines || []).map((p) => {
        const one = (p.candidates || []).length === 1 ? p.candidates[0] : null;
        return { ...emptyLine(), product_id: one?.id || "", product_name: one?.name || p.text || "", qty: p.qty ?? "",
          free_qty: p.free_qty ?? "", free_manual: p.free_qty != null };
      });
      const ls = [...so.lines.map((l) => ({ ...emptyLine(), ...l, mf_ratio: l.mf_ratio || "", free_qty: l.free_qty || "", free_manual: !!l.free_manual })), ...pend];
      setLines(ls.length ? ls : [emptyLine()]);
    }).catch((e) => setErr(apiError(e, "Sipariş yüklenemedi")));
  }, [id]);

  const pickCustomer = ({ value, item }) => {
    setCustText(value || "");
    if (!item) { setCust(null); return; }
    setCust(item);
    setF((p) => ({ ...p, due_days: item.default_payment_days || 0 }));
    const field = PRICE_FIELD[item.price_group || item.customer_type];
    setLines((ls) => ls.map((l) => {
      const p = products.find((x) => x.id === l.product_id);
      if (!p) return l;
      const price = Number(p[field] || 0);
      return { ...l, unit_price: price, list_price: price };
    }));
  };
  const group = cust?.price_group || cust?.customer_type;
  const ready = useMemo(() => lines.filter((l) => l.product_id), [lines]);

  const save = async () => {
    if (!cust) return setErr("Müşteri seçiniz");
    if (lines.some((l) => !l.product_id && (l.product_name || "").trim())) return setErr("Eşleşmeyen ürün satırı var — listeden ürün seçin veya satırı silin");
    if (!ready.length || ready.some((l) => !(Number(l.qty) > 0))) return setErr("En az bir ürün satırı ve pozitif adet giriniz");
    const payload = {
      customer_id: cust.id, customer_ref: f.customer_ref, order_date: f.order_date, delivery_date: f.delivery_date || null,
      due_days: num(f.due_days), show_discount: f.show_discount, notes: f.notes, source: src.source,
      lines: ready.map((l) => ({ product_id: l.product_id, qty: Number(l.qty), mf_ratio: l.mf_ratio || null,
        free_qty: l.free_manual ? num(l.free_qty) ?? 0 : null, unit_price: num(l.unit_price),
        discount_pct: Number(l.discount_pct) || 0, vat_pct: num(l.vat_pct) })),
    };
    setBusy(true); setErr("");
    try {
      const so = id ? await api.put(`/sales-orders/${id}`, payload) : await api.post("/sales-orders", payload);
      nav(`/sales-orders/${so.id}`);
    } catch (e) { setErr(apiError(e, "Kaydedilemedi")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="so-form-page">
      <PageHeader title={id ? "Siparişi Düzenle" : "Yeni Satış Siparişi"} description="Fiyatlar müşteri tipine göre ürün kartından gelir; siparişteki değişiklik ürün kartını değiştirmez." />
      <TelegramReviewBanner src={src} />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="p-4 lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <SmartSuggestInput label="Müşteri *" value={custText} items={customers} displayField="legal_name" secondaryField="code" onChange={pickCustomer} placeholder="Müşteri ara…" data-testid="so-form-customer" />
            {cust && <div className="text-xs text-slate-600 mt-1 flex items-center gap-2" data-testid="so-form-customer-info"><CustomerTypeBadge type={group} /> {cust.billing_address || "Adres yok"}</div>}
          </div>
          <Input label="Müşteri Sipariş / Referans No" value={f.customer_ref} onChange={(e) => setF({ ...f, customer_ref: e.target.value })} data-testid="so-form-ref" />
          <TrDateField label="Sipariş Tarihi" value={f.order_date} onChange={(v) => setF({ ...f, order_date: v })} testid="so-form-date" />
          <TrDateField label="Teslim Tarihi" value={f.delivery_date} onChange={(v) => setF({ ...f, delivery_date: v })} testid="so-form-delivery" />
          <Input label="Vade Günü" type="number" min="0" value={f.due_days} onChange={(e) => setF({ ...f, due_days: e.target.value })} data-testid="so-form-due-days" />
          <label className="flex items-center gap-2 text-sm md:col-span-3" data-testid="so-form-show-discount-label">
            <input type="checkbox" checked={f.show_discount} onChange={(e) => setF({ ...f, show_discount: e.target.checked })} data-testid="so-form-show-discount" /> İskontoyu Göster (Proforma PDF)
          </label>
        </Card>
        <TotalsBox lines={ready} f={f} />
      </div>
      <Card className="p-4 mt-4">
        {!cust && <div className="text-xs text-amber-700 mb-2">Fiyatların doğru gelmesi için önce müşteri seçin.</div>}
        <OrderLinesEditor lines={lines} setLines={setLines} products={products} priceGroup={group} vatDefault={vatDefault} />
        <div className="mt-3"><Textarea label="Not" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="so-form-notes" /></div>
      </Card>
      {err && <div className="mt-3 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="so-form-error">{err}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={() => nav(id ? `/sales-orders/${id}` : "/sales-orders")} data-testid="so-form-cancel">Vazgeç</Btn>
        <Btn onClick={save} disabled={busy} data-testid="so-form-save">{busy ? "Kaydediliyor…" : "Kaydet"}</Btn>
      </div>
    </div>
  );
}
